/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions
} from '@tanstack/react-query';
import {subscriptionsApiClient} from '#lib/api-client';
import {queryKeys} from '#lib/query-client';
import {STALE_TIME, GC_TIME} from '#constants';

// Hook for getting subscriptions
export function useSubscriptions(
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.subscriptions.list(params),
    queryFn: () => subscriptionsApiClient.getSubscriptions(params),
    staleTime: STALE_TIME.SUBSCRIPTIONS,
    gcTime: GC_TIME.SUBSCRIPTIONS,
    ...options
  });
}

// Hook for unsubscribe mutation
export function useUnsubscribe(
  options?: UseMutationOptions<void, Error, {id: number}>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({id}) => {
      await subscriptionsApiClient.unsubscribe(id);
    },
    onSuccess: () => {
      // Invalidate subscriptions queries
      queryClient.invalidateQueries({queryKey: queryKeys.subscriptions.all});
    },
    ...options
  });
}

// Hook for bulk unsubscribe mutation
export function useBulkUnsubscribe(
  options?: UseMutationOptions<void, Error, {ids: number[]}>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ids}) => {
      await subscriptionsApiClient.bulkUnsubscribe(ids);
    },
    onSuccess: () => {
      // Invalidate subscriptions queries
      queryClient.invalidateQueries({queryKey: queryKeys.subscriptions.all});
    },
    ...options
  });
}

// Hook for accept invitation mutation
export function useAcceptInvitation(
  options?: UseMutationOptions<void, Error, {id: number}>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({id}) => {
      await subscriptionsApiClient.acceptInvitation(id);
    },
    onSuccess: () => {
      // Invalidate subscriptions queries
      queryClient.invalidateQueries({queryKey: queryKeys.subscriptions.all});
    },
    ...options
  });
}

// Hook for decline invitation mutation
export function useDeclineInvitation(
  options?: UseMutationOptions<void, Error, {id: number}>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({id}) => {
      await subscriptionsApiClient.declineInvitation(id);
    },
    onSuccess: () => {
      // Invalidate subscriptions queries
      queryClient.invalidateQueries({queryKey: queryKeys.subscriptions.all});
    },
    ...options
  });
}
